import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../api/auth";
import { useUserStore } from "../context/UserContext";

export default function UserNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedUser } = useUserStore();

  const [showNavbar, setShowNavbar] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 991 : false
  );
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);

  const leftMenuRef = useRef(null);
  const userRef = useRef(null);

  const userName = selectedUser?.name || "User";
  const userEmail = selectedUser?.email || "user@gmail.com";

  /* RESPONSIVE WIDTH CONTROL */
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 991;
      setShowNavbar(isMobile);

      if (!isMobile) {
        setLeftMenuOpen(false);
        setUserDropOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  /* CLOSE DROPDOWNS ON ROUTE CHANGE */
  useEffect(() => {
    setLeftMenuOpen(false);
    setUserDropOpen(false);
  }, [location.pathname]);

  /* OUTSIDE CLICK CLOSE - FIXED TYPO HERE */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
        setLeftMenuOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserDropOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside); // ← FIXED: was handleOutside
    };
  }, []);

  /* LOGOUT */
  const handleLogout = () => {
    logoutUser();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  /* HIDE ON DESKTOP */
  if (!showNavbar) return null;

  return (
    <nav className="mobile-navbar">
      {/* LEFT SIDE: MENU + BRAND */}
      <div className="left-section" ref={leftMenuRef}>
        <button
          className="menu-toggle"
          aria-label="Open menu"
          onClick={() => {
            setLeftMenuOpen((p) => !p);
            setUserDropOpen(false);
          }}
        >
          <i className="fas fa-bars"></i>
        </button>

        <span className="brand-name">Manager Panel</span>

        {leftMenuOpen && (
          <div className="menu-dropdown">
            <NavLink to="/userdashboard" className="menu-link">
              <i className="fas fa-home"></i>
              <span>Dashboard</span>
            </NavLink>

            <NavLink to="/notify" className="menu-link">
              <i className="fas fa-bell"></i>
              <span>Notifications</span>
            </NavLink>

            <NavLink to="/viplist" className="menu-link">
              <i className="fas fa-table"></i>
              <span>VIP Table</span>
            </NavLink>

            <NavLink to="/guardlist" className="menu-link">
              <i className="fas fa-table"></i>
              <span>Guard Table</span>
            </NavLink>

            <NavLink to="/createsecuritygroup" className="menu-link">
              <i className="fas fa-cog"></i>
              <span>Create Security Groups</span>
            </NavLink>
            <NavLink to="/vgmang" className="menu-link">
              <i className="fas fa-users-cog"></i>
              <span>VIP–Guard Management</span>
            </NavLink>
            <NavLink to="/adminrequestaccept" className="menu-link">
              <i className="fas fa-check-circle"></i>
              <span>Duty Accept/Reject</span>
            </NavLink>

            <NavLink to="/dutyhistory" className="menu-link">
              <i className="fas fa-history"></i>
              <span>Officer Duty History</span>
            </NavLink>

            <NavLink to="/incidents" className="menu-link">
              <i className="fas fa-exclamation-triangle"></i>
              <span>Incidents</span>
            </NavLink>

             <NavLink to="/complaints" className="menu-link">
              <i className="fas fa-comment-alt"></i>
              <span>Complaints</span>
            </NavLink> 

            <NavLink to="/feedback" className="menu-link">
              <i className="fas fa-star"></i>
              <span>Feedback</span>
            </NavLink>

            <NavLink to="/userprofile" className="menu-link">
              <i className="fas fa-user"></i>
              <span>My Profile</span>
            </NavLink>
          </div>
        )}
      </div>

      {/* RIGHT SIDE: PROFILE */}
      <div className="right-section" ref={userRef}>
        <button
          className="profile-toggle"
          aria-label="Open profile"
          onClick={() => {
            setUserDropOpen((p) => !p);
            setLeftMenuOpen(false);
          }}
        >
          <img
            src="/assets/img/avtar.jpg"
            alt="User"
            className="avatar"
          />
        </button>

        {userDropOpen && (
          <div className="profile-dropdown">
            <div className="profile-header">
              <img
                src="/assets/img/avtar.jpg"
                alt="User"
                className="profile-avatar"
              />
              <div className="profile-info">
                <strong>{userName}</strong>
                <small>{userEmail}</small>
              </div>
            </div>

            <NavLink to="/userprofile" className="profile-link">
              <i className="fas fa-user"></i>
              <span>My Profile</span>
            </NavLink>

            <button onClick={handleLogout} className="logout-button">
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Premium Dark Glassmorphic Styling */}
      <style jsx>{`
        .mobile-navbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.8rem 1rem;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
          position: sticky;
          top: 0;
          z-index: 1000;
          width: 100%;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        }

        .left-section {
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
        }

        .menu-toggle {
          background: none;
          border: none;
          color: #60a5fa;
          font-size: 1.6rem;
          cursor: pointer;
          padding: 0.4rem;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .menu-toggle:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: scale(1.1);
        }

        .brand-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: #34d399;
          letter-spacing: 0.5px;
        }

        .menu-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          width: 260px;
          max-height: 70vh;
          overflow-y: auto;
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(16px);
          border-radius: 14px;
          border: 1px solid rgba(16, 185, 129, 0.3);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          margin-top: 0.5rem;
          animation: slideDown 0.3s ease-out;
          z-index: 999;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .menu-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.9rem 1.2rem;
          color: #cbd5e1;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.3s ease;
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
        }

        .menu-link:hover {
          background: rgba(16, 185, 129, 0.15);
          color: #ffffff;
          padding-left: 1.5rem;
        }

        .menu-link i {
          font-size: 1.1rem;
          width: 24px;
          text-align: center;
          color: #34d399;
        }

        .right-section {
          position: relative;
        }

        .profile-toggle {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 2px solid #34d399;
          object-fit: cover;
          box-shadow: 0 0 10px rgba(52, 211, 153, 0.4);
          transition: transform 0.3s ease;
        }

        .profile-toggle:hover .avatar {
          transform: scale(1.1);
        }

        .profile-dropdown {
          position: absolute;
          right: 0;
          top: 100%;
          width: 260px;
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(16px);
          border-radius: 14px;
          border: 1px solid rgba(16, 185, 129, 0.3);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          margin-top: 0.5rem;
          overflow: hidden;
          animation: slideDown 0.3s ease-out;
          z-index: 999;
        }

        .profile-header {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          padding: 1rem;
          background: rgba(16, 185, 129, 0.15);
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .profile-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 2px solid #34d399;
        }

        .profile-info {
          color: #e2e8f0;
        }

        .profile-info strong {
          display: block;
          font-size: 1rem;
          font-weight: 600;
        }

        .profile-info small {
          opacity: 0.8;
          font-size: 0.85rem;
        }

        .profile-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.9rem 1.2rem;
          color: #cbd5e1;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .profile-link:hover {
          background: rgba(16, 185, 129, 0.15);
          color: #ffffff;
          padding-left: 1.5rem;
        }

        .profile-link i {
          color: #34d399;
          font-size: 1.1rem;
          width: 24px;
          text-align: center;
        }

        .logout-button {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 0.9rem 1.2rem;
          background: #ef4444;
          color: white;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .logout-button:hover {
          background: #dc2626;
          padding-left: 1.5rem;
        }

        .logout-button i {
          font-size: 1.1rem;
          width: 24px;
          text-align: center;
        }

        /* Scrollbar styling */
        .menu-dropdown::-webkit-scrollbar,
        .profile-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .menu-dropdown::-webkit-scrollbar-thumb,
        .profile-dropdown::-webkit-scrollbar-thumb {
          background: rgba(52, 211, 153, 0.4);
          border-radius: 3px;
        }
      `}</style>
    </nav>
  );
}
// import React, { useState, useEffect, useRef } from "react";
// import { NavLink, useNavigate, useLocation } from "react-router-dom";
// import { logoutUser } from "../api/auth";
// import { useUserStore } from "../context/UserContext";

// export default function UserNavbar() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { selectedUser } = useUserStore();

//   const [showNavbar, setShowNavbar] = useState(
//     typeof window !== "undefined" ? window.innerWidth <= 991 : false
//   );
//   const [leftMenuOpen, setLeftMenuOpen] = useState(false);
//   const [userDropOpen, setUserDropOpen] = useState(false);

//   const leftMenuRef = useRef(null);
//   const userRef = useRef(null);

//   const userName = selectedUser?.name || "User";
//   const userEmail = selectedUser?.email || "user@gmail.com";

//   /*  RESPONSIVE WIDTH CONTROL */
//   useEffect(() => {
//     const handleResize = () => {
//       const isMobile = window.innerWidth <= 991;
//       setShowNavbar(isMobile);

//       if (!isMobile) {
//         setLeftMenuOpen(false);
//         setUserDropOpen(false);
//       }
//     };

//     handleResize();
//     window.addEventListener("resize", handleResize);
//     window.addEventListener("orientationchange", handleResize);

//     return () => {
//       window.removeEventListener("resize", handleResize);
//       window.removeEventListener("orientationchange", handleResize);
//     };
//   }, []);

//   /*  CLOSE DROPDOWNS ON ROUTE CHANGE */
//   useEffect(() => {
//     setLeftMenuOpen(false);
//     setUserDropOpen(false);
//   }, [location.pathname]);

//   /*  OUTSIDE CLICK CLOSE */
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
//         setLeftMenuOpen(false);
//       }
//       if (userRef.current && !userRef.current.contains(e.target)) {
//         setUserDropOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     document.addEventListener("touchstart", handleClickOutside);

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//       document.removeEventListener("touchstart", handleClickOutside);
//     };
//   }, []);

//   /*  LOGOUT */
//   const handleLogout = () => {
//     logoutUser();
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   /*  HIDE ON DESKTOP */
//   if (!showNavbar) return null;

//   return (
//     <nav style={styles.navbar}>
//       {/*  LEFT SIDE : MENU + BRAND */}
//       <div style={styles.leftBox} ref={leftMenuRef}>
//         <button
//           style={styles.menuBtn}
//           aria-label="Open menu"
//           onClick={() => {
//             setLeftMenuOpen((p) => !p);
//             setUserDropOpen(false);
//           }}
//         >
//           ☰
//         </button>

//         <span style={styles.brand}>Manger Panel</span>

//         {leftMenuOpen && (
//           <div style={styles.leftDropdown}>
//             <NavLink to="/userdashboard" style={styles.menuItem}>
//               Dashboard
//             </NavLink>
            
//             <NavLink to="/notify" style={styles.menuItem}>
//               Notifications
//             </NavLink>

//             <NavLink to="/viplist" style={styles.menuItem}>
//               VIP Table
//             </NavLink>

//             <NavLink to="/guardlist" style={styles.menuItem}>
//               Guard Table
//             </NavLink>

//             <NavLink to="/vgmang" style={styles.menuItem}>
//               VIP–Guard Management
//             </NavLink>

//             <NavLink to="/dutyhistory" style={styles.menuItem}>
//               Guard Duty History
//             </NavLink>

//             <NavLink to="/complaints" style={styles.menuItem}>
//               Complaints
//             </NavLink>

//             <NavLink to="/feedback" style={styles.menuItem}>
//               Feedback
//             </NavLink>

//             {/* <NavLink to="/updatehistory" style={styles.menuItem}>
//               Updation History
//             </NavLink> */}
            
//             <NavLink to="/userprofile" style={styles.menuItem}>
//               My Profile
//             </NavLink>

//             <NavLink to="/incidents" style={styles.menuItem}>
//               Incidents
//             </NavLink>
            
           
//           </div>
//         )}
//       </div>

//       {/*  RIGHT SIDE : PROFILE ICON */}
//       <div style={styles.userBox} ref={userRef}>
//         <button
//           style={styles.profileIconBtn}
//           aria-label="Open profile"
//           onClick={() => {
//             setUserDropOpen((p) => !p);
//             setLeftMenuOpen(false);
//           }}
//         >
//           <img
//             src="/assets/img/avtar.jpg"
//             alt="User"
//             style={styles.avatar}
//           />
//         </button>

//         {userDropOpen && (
//           <div style={styles.userDropdown}>
//             <div style={styles.userHeader}>
//               <img
//                 src="/assets/img/avtar.jpg"
//                 alt="User"
//                 style={styles.userBigAvatar}
//               />
//               <div>
//                 <strong>{userName}</strong>
//                 <br />
//                 <small>{userEmail}</small>
//               </div>
//             </div>

//             <NavLink to="/userprofile" style={styles.userItem}>
//               My Profile
//             </NavLink>

//             <button onClick={handleLogout} style={styles.logoutBtn}>
//               Logout
//             </button>
//           </div>
//         )}
//       </div>
//     </nav>
//   );
// }

// /* ===============================
//     USER NAVBAR STYLES (RESPONSIVE)
// ================================ */

// const styles = {
//   navbar: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     padding: "10px 14px",
//     background: "#ffffff",
//     borderBottom: "1px solid #ddd",
//     position: "sticky",
//     top: 0,
//     zIndex: 1000,
//     width: "100%",
//   },

//   /* LEFT */
//   leftBox: {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     position: "relative",
//     maxWidth: "70%",
//     flexWrap: "nowrap",
//   },

//   menuBtn: {
//     fontSize: 20,
//     fontWeight: "900",
//     background: "none",
//     border: "2px solid #4e54c8",
//     borderRadius: 6,
//     padding: "2px 8px",
//     cursor: "pointer",
//     color: "#4e54c8",
//     minWidth: 36,
//     minHeight: 32,
//   },

//   brand: {
//     fontSize: 16,
//     fontWeight: "700",
//     color: "#4e54c8",
//     whiteSpace: "nowrap",
//   },

//   leftDropdown: {
//     position: "absolute",
//     top: 38,
//     left: 0,
//     width: 240,
//     maxHeight: "70vh",
//     overflowY: "auto",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
//     zIndex: 1000,
//   },

//   menuItem: {
//     display: "block",
//     padding: "12px 14px",
//     textDecoration: "none",
//     color: "#333",
//     borderBottom: "1px solid #eee",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   /* RIGHT */
//   userBox: {
//     position: "relative",
//     flexShrink: 0,
//   },

//   profileIconBtn: {
//     background: "none",
//     border: "none",
//     padding: 0,
//     cursor: "pointer",
//   },

//   avatar: {
//     width: 34,
//     height: 34,
//     borderRadius: "50%",
//     border: "2px solid #4e54c8",
//     objectFit: "cover",
//   },

//   userDropdown: {
//     position: "absolute",
//     right: 0,
//     top: 42,
//     width: 220,
//     maxWidth: "90vw",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
//     overflow: "hidden",
//     zIndex: 1000,
//   },

//   userHeader: {
//     display: "flex",
//     gap: 10,
//     padding: 12,
//     background: "#f8f9fa",
//     borderBottom: "1px solid #eee",
//     alignItems: "center",
//   },

//   userBigAvatar: {
//     width: 38,
//     height: 38,
//     borderRadius: "50%",
//   },

//   userItem: {
//     display: "block",
//     padding: "10px 14px",
//     textDecoration: "none",
//     color: "#333",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   logoutBtn: {
//     width: "100%",
//     border: "none",
//     background: "#dc3545",
//     color: "#fff",
//     padding: 12,
//     fontWeight: 700,
//     cursor: "pointer",
//     fontSize: 14,
//   },
// };
